import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Edit, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import { BOOKING_FUNNEL_TAG_COLORS, BookingFunnelTagColor, getTagColorClassName } from '@/lib/booking-funnel-tag-colors';

type TagFormData = {
    labelDe: string;
    labelEn: string;
    color: BookingFunnelTagColor;
};

const emptyFormData: TagFormData = {
    labelDe: '',
    labelEn: '',
    color: 'BURGUNDY',
};

export const TagPreview = ({ color, label }: { color: string; label: string }) => (
    <span className={`inline-flex items-center justify-center rounded-full px-2 py-1 text-center text-xs font-medium leading-none ${getTagColorClassName(color)}`}>
        {label}
    </span>
);

const TagsEditor = () => {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [editingTagId, setEditingTagId] = useState<string | null>(null);
    const [formData, setFormData] = useState<TagFormData>(emptyFormData);

    const utils = trpc.useUtils();
    const { data: tagsData, isLoading } = trpc.bookingFunnelCMS.getTags.useQuery();
    const tags = tagsData?.data || [];

    const invalidate = () => {
        utils.bookingFunnelCMS.getTags.invalidate();
        utils.bookingFunnelCMS.getBookableItems.invalidate();
    };

    const createMutation = trpc.bookingFunnelCMS.createTag.useMutation({
        onSuccess: () => {
            toast.success('Tag erstellt');
            invalidate();
            setIsCreateDialogOpen(false);
            setFormData(emptyFormData);
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen', { description: error.message });
        },
    });

    const updateMutation = trpc.bookingFunnelCMS.updateTag.useMutation({
        onSuccess: () => {
            toast.success('Tag gespeichert');
            invalidate();
            setEditingTagId(null);
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    const deleteMutation = trpc.bookingFunnelCMS.deleteTag.useMutation({
        onSuccess: () => {
            toast.success('Tag gelöscht');
            invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen', { description: error.message });
        },
    });

    const openCreateDialog = () => {
        setFormData(emptyFormData);
        setIsCreateDialogOpen(true);
    };

    const openEditDialog = (tag: { id: string; labelDe: string; labelEn: string; color: string }) => {
        setFormData({
            labelDe: tag.labelDe,
            labelEn: tag.labelEn,
            color: tag.color as BookingFunnelTagColor,
        });
        setEditingTagId(tag.id);
    };

    const handleCreate = () => {
        if (!formData.labelDe.trim() || !formData.labelEn.trim()) {
            toast.error('Bitte geben Sie einen deutschen und englischen Text ein');
            return;
        }
        createMutation.mutate(formData);
    };

    const handleSave = () => {
        if (!editingTagId) return;
        if (!formData.labelDe.trim() || !formData.labelEn.trim()) {
            toast.error('Bitte geben Sie einen deutschen und englischen Text ein');
            return;
        }
        updateMutation.mutate({ id: editingTagId, ...formData });
    };

    const handleDelete = (tag: { id: string; labelDe: string; _count: { bookableItems: number } }) => {
        const usage = tag._count.bookableItems;
        const message = usage > 0
            ? `"${tag.labelDe}" wird aktuell von ${usage} Leistung(en) verwendet und verschwindet dort. Wirklich löschen?`
            : `"${tag.labelDe}" wirklich löschen?`;
        if (confirm(message)) {
            deleteMutation.mutate({ id: tag.id });
        }
    };

    const formFields = (
        <div className='space-y-4'>
            <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                    <Label>Text (Deutsch)</Label>
                    <Input
                        value={formData.labelDe}
                        maxLength={30}
                        placeholder='z.B. Neu'
                        onChange={(e) => setFormData({ ...formData, labelDe: e.target.value })}
                    />
                </div>
                <div className='space-y-2'>
                    <Label>Text (Englisch)</Label>
                    <Input
                        value={formData.labelEn}
                        maxLength={30}
                        placeholder='e.g. New'
                        onChange={(e) => setFormData({ ...formData, labelEn: e.target.value })}
                    />
                </div>
            </div>
            <div className='space-y-2'>
                <Label>Farbe</Label>
                <Select
                    value={formData.color}
                    onValueChange={(value) => setFormData({ ...formData, color: value as BookingFunnelTagColor })}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {BOOKING_FUNNEL_TAG_COLORS.map((color) => (
                            <SelectItem key={color.value} value={color.value}>
                                <span className='flex items-center gap-2'>
                                    <span className={`h-4 w-4 rounded-full ${color.className}`} />
                                    {color.label}
                                </span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className='space-y-2'>
                <Label>Vorschau</Label>
                <div className='rounded-md border border-gray-200 bg-gray-50 p-4'>
                    <TagPreview color={formData.color} label={formData.labelDe || 'Neu'} />
                </div>
            </div>
        </div>
    );

    if (isLoading) {
        return (
            <div className='space-y-3'>
                <Skeleton className='h-10 w-40' />
                <Skeleton className='h-40 w-full' />
            </div>
        );
    }

    return (
        <div className='space-y-4'>
            <div className='flex items-start justify-between gap-4'>
                <p className='text-muted-foreground text-sm max-w-2xl'>
                    Tags werden im Buchungs-Funnel oben rechts auf der Karte der buchbaren Leistung angezeigt.
                    Die Zuordnung erfolgt je Leistung im Tab „Grunddaten“.
                </p>
                <Button onClick={openCreateDialog}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neuer Tag
                </Button>
            </div>

            {tags.length === 0 ? (
                <Card>
                    <CardContent className='py-12 text-center text-muted-foreground'>
                        Noch keine Tags erstellt. Klicken Sie auf „Neuer Tag“ um zu beginnen.
                    </CardContent>
                </Card>
            ) : (
                <div className='bg-white rounded-lg shadow-sm p-6'>
                    <div className='rounded-md border border-gray-200 overflow-hidden'>
                        <Table>
                            <TableHeader className='bg-gray-50'>
                                <TableRow>
                                    <TableHead className='font-medium text-gray-700'>Vorschau</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Deutsch</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Englisch</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Verwendet von</TableHead>
                                    <TableHead className='font-medium text-gray-700 w-[100px]'>Aktionen</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {tags.map((tag) => (
                                    <TableRow key={tag.id} className='hover:bg-gray-50 transition-colors'>
                                        <TableCell>
                                            <TagPreview color={tag.color} label={tag.labelDe} />
                                        </TableCell>
                                        <TableCell className='font-medium'>{tag.labelDe}</TableCell>
                                        <TableCell>{tag.labelEn}</TableCell>
                                        <TableCell className='text-muted-foreground'>
                                            {tag._count.bookableItems} Leistung(en)
                                        </TableCell>
                                        <TableCell>
                                            <div className='flex gap-1'>
                                                <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={() => openEditDialog(tag)}
                                                    title='Bearbeiten'
                                                >
                                                    <Edit className='h-4 w-4' />
                                                </Button>
                                                <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={() => handleDelete(tag)}
                                                    disabled={deleteMutation.isPending}
                                                    title='Löschen'
                                                >
                                                    <Trash2 className='h-4 w-4 text-destructive' />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Neuer Tag</DialogTitle>
                        <DialogDescription>
                            Erstellen Sie einen Tag, den Sie anschließend beliebig vielen Leistungen zuordnen können.
                        </DialogDescription>
                    </DialogHeader>
                    {formFields}
                    <DialogFooter>
                        <Button variant='outline' onClick={() => setIsCreateDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleCreate} disabled={createMutation.isPending}>
                            Erstellen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={editingTagId !== null} onOpenChange={(open) => !open && setEditingTagId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Tag bearbeiten</DialogTitle>
                        <DialogDescription>
                            Änderungen wirken sich sofort auf alle Leistungen aus, die diesen Tag verwenden.
                        </DialogDescription>
                    </DialogHeader>
                    {formFields}
                    <DialogFooter>
                        <Button variant='outline' onClick={() => setEditingTagId(null)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleSave} disabled={updateMutation.isPending}>
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TagsEditor;
